<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import customParseFormat from 'dayjs/plugin/customParseFormat'

import { useDetectDevice } from '@/composables/device'
import { usePortalStore } from '@/stores/portal.store'
import {
  dateRangePresetDefinitions,
  clampToPresets,
  rangeForPreset,
  quarterStart,
  quarterEnd,
  semesterStart,
  semesterEnd,
} from '@/composables/date-range-presets'

dayjs.extend(isoWeek)
dayjs.extend(customParseFormat)

const portalStore = usePortalStore()
const { isMobile } = useDetectDevice()
const PRESET = portalStore.dateRangePresetEnum

const props = defineProps({
  modelValue: {
    type: Object, // { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }
    required: true,
  },
  defaultPreset: {
    type: Number,
    default: null,
  },
  allowedPresets: {
    type: Array,
    default: null,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  pickerOnly: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['update:modelValue', 'preset'])

const { t, locale } = useI18n()

// --- Locale-aware date formats via Intl ---
const getLocaleDateFmt = (localeStr) => {
  const parts = new Intl.DateTimeFormat(localeStr, { year: '2-digit', month: '2-digit', day: '2-digit' })
    .formatToParts(new Date(2001, 0, 15))
  const short = parts.map(p => {
    if (p.type === 'day')   return 'DD'
    if (p.type === 'month') return 'MM'
    if (p.type === 'year')  return 'YY'
    return p.value
  }).join('')
  return { short, long: short.replace('YY', 'YYYY') }
}

const dateFmt = computed(() => getLocaleDateFmt(locale.value))
const inputFormats = computed(() => {
  const { short, long } = dateFmt.value
  return [short, long, ...[short, long].map(fmt => fmt.replace('DD', 'D').replace('MM', 'M'))]
})

const RANGE_SEPARATOR = /\s*[–—]\s*|\s+-\s+/
const splitRange = (text) => {
  const parts = text.split(RANGE_SEPARATOR)
  return parts.length === 1 && !dateFmt.value.short.includes('-') ? text.split('-') : parts
}

const presets = computed(() => dateRangePresetDefinitions
  .filter(p => (p.selectable && (props.allowedPresets?.includes(p.value) ?? true)) || p.value === activePreset.value)
  .map(p => ({
    value: p.value,
    label: p.labelCount != null ? t(p.labelKey, p.labelCount) : t(p.labelKey),
    icon: p.icon,
  })))

// --- Internal state ---
const activePreset = ref(props.defaultPreset === PRESET.CUSTOM ? PRESET.CUSTOM : clampToPresets(props.defaultPreset ?? PRESET.TWO_WEEKS, props.allowedPresets))

const activePresetLabel = computed(() => presets.value.find(p => p.value === activePreset.value)?.label)

// q-date range model uses slashes: { from: 'YYYY/MM/DD', to: 'YYYY/MM/DD' }
const dateRange = ref({
  from: props.modelValue.from.replaceAll('-', '/'),
  to:   props.modelValue.to.replaceAll('-', '/'),
})

// --- Helpers ---
const toSlash = (d) => dayjs(d).format('YYYY/MM/DD')
const toDash  = (d) => dayjs(d).format('YYYY-MM-DD')

const emitRange = (from, to) => {
  dateRange.value = { from: toSlash(from), to: toSlash(to) }
  emit('update:modelValue', { from: toDash(from), to: toDash(to) })
}

const switchToCustom = () => {
  if (activePreset.value === PRESET.CUSTOM) return
  activePreset.value = PRESET.CUSTOM
  emit('preset', PRESET.CUSTOM)
}

// --- Apply preset ---
const applyPreset = (preset) => {
  activePreset.value = preset
  emit('preset', preset)
  const range = rangeForPreset(preset)
  if (range) emitRange(range.from, range.to)
}

// --- Chevron navigation ---
const navigate = (direction) => {
  const from = dayjs(props.modelValue.from)

  if (activePreset.value === PRESET.WEEK) {
    const newFrom = from.add(direction, 'week').startOf('isoWeek')
    emitRange(newFrom, newFrom.endOf('isoWeek'))
  } else if (activePreset.value === PRESET.MONTH) {
    const newFrom = from.add(direction, 'month').startOf('month')
    emitRange(newFrom, newFrom.endOf('month'))
  } else if (activePreset.value === PRESET.QUARTER) {
    const newFrom = quarterStart(from.add(direction * 3, 'month'))
    emitRange(newFrom, quarterEnd(newFrom))
  } else if (activePreset.value === PRESET.SEMESTER) {
    const newFrom = semesterStart(from.add(direction * 6, 'month'))
    emitRange(newFrom, semesterEnd(newFrom))
  } else if (activePreset.value === PRESET.YEAR) {
    const newFrom = from.add(direction, 'year').startOf('year')
    emitRange(newFrom, newFrom.endOf('year'))
  } else {
    const to  = dayjs(props.modelValue.to)
    const days = to.diff(from, 'day') + 1
    emitRange(from.add(direction * days, 'day'), to.add(direction * days, 'day'))
  }
}

// --- Sync q-date picker changes to parent ---
// q-date emits a plain 'YYYY/MM/DD' string when the range is a single day
watch(dateRange, (val) => {
  const range = typeof val === 'string' ? { from: val, to: val } : val
  if (!range?.from || !range?.to) return
  const f = toDash(range.from)
  const t2 = toDash(range.to)
  if (f !== props.modelValue.from || t2 !== props.modelValue.to) {
    switchToCustom()
    emit('update:modelValue', { from: f, to: t2 })
  }
})

// --- Display / editable text ---
const formatRange = (from, to) => {
  const fmt = dateFmt.value.short
  return `${dayjs(from).format(fmt)} – ${dayjs(to).format(fmt)}`
}

const inputText = ref(formatRange(props.modelValue.from, props.modelValue.to))
const isEditing = ref(false)

watch(
  () => props.modelValue,
  (val) => { if (!isEditing.value) inputText.value = formatRange(val.from, val.to) },
)

// Re-format when locale changes (and not editing)
watch(dateFmt, () => {
  if (!isEditing.value) inputText.value = formatRange(props.modelValue.from, props.modelValue.to)
})

const commitInput = () => {
  isEditing.value = false
  const parts = splitRange(inputText.value).map(s => s.trim())
  if (parts.length !== 2) { inputText.value = formatRange(props.modelValue.from, props.modelValue.to); return }
  const [f, t2] = parts.map(s => dayjs(s, inputFormats.value, true))
  if (!f.isValid() || !t2.isValid() || f.isAfter(t2)) {
    inputText.value = formatRange(props.modelValue.from, props.modelValue.to)
    return
  }
  switchToCustom()
  emitRange(f, t2)
}

</script>

<template>
  <div class="date-range-navigator row items-center" :class="{ 'date-range-navigator--stacked': isMobile && !pickerOnly && !compact }">
    <template v-if="pickerOnly">
      <q-btn
        icon="mdi-chevron-left"
        flat
        round
        dense
        color="primary"
        @click="navigate(-1)"
      />
      <q-btn
        flat
        dense
        no-caps
        class="col date-range-navigator__range"
        :label="formatRange(modelValue.from, modelValue.to)"
      >
        <q-popup-proxy cover transition-show="scale" transition-hide="scale">
          <q-date
            v-model="dateRange"
            range
            mask="YYYY/MM/DD"
            today-btn
          />
        </q-popup-proxy>
      </q-btn>
      <q-btn
        icon="mdi-chevron-right"
        flat
        round
        dense
        color="primary"
        @click="navigate(1)"
      />
    </template>

    <!-- Compact: the active range between the chevrons, then the period dropdown -->
    <template v-if="!pickerOnly && compact">
      <q-btn
        icon="mdi-chevron-left"
        flat
        round
        dense
        color="primary"
        @click="navigate(-1)"
      />
      <q-btn
        flat
        dense
        no-caps
        color="primary"
        class="date-range-navigator__range"
        :label="formatRange(modelValue.from, modelValue.to)"
      >
        <q-popup-proxy cover transition-show="scale" transition-hide="scale">
          <q-date
            v-model="dateRange"
            range
            mask="YYYY/MM/DD"
            today-btn
          />
        </q-popup-proxy>
      </q-btn>
      <q-btn
        icon="mdi-chevron-right"
        flat
        round
        dense
        color="primary"
        @click="navigate(1)"
      />
      <q-space />
      <q-btn-dropdown
        flat
        dense
        no-caps
        color="primary"
        class="date-range-navigator__period"
        :label="activePresetLabel"
      >
        <q-list dense>
          <q-item
            v-for="preset in presets"
            :key="preset.value"
            v-close-popup
            clickable
            :active="preset.value === activePreset"
            @click="applyPreset(preset.value)"
          >
            <q-item-section avatar>
              <q-icon :name="preset.icon" />
            </q-item-section>
            <q-item-section>{{ preset.label }}</q-item-section>
          </q-item>
        </q-list>
      </q-btn-dropdown>
    </template>

    <!-- Preset select -->
    <q-select
      v-if="!pickerOnly && !compact"
      :model-value="activePreset"
      :options="presets"
      :label="t('common.period', 1)"
      option-value="value"
      option-label="label"
      dense
      outlined
      emit-value
      map-options
      hide-bottom-space
      class="date-range-navigator__preset"
      @update:model-value="applyPreset"
    />

    <!-- Chevron prev (full layout: before the range input) -->
    <q-btn
      v-if="!pickerOnly && !compact"
      icon="mdi-chevron-left"
      flat
      round
      dense
      color="primary"
      @click="navigate(-1)"
    />

    <!-- Date range input → opens q-date picker or manual edit -->
    <q-input
      v-if="!pickerOnly && !compact"
      v-model="inputText"
      dense
      outlined
      hide-bottom-space
      class="date-range-navigator__input"
      @focus="isEditing = true"
      @blur="commitInput"
      @keyup.enter="commitInput"
    >
      <template #prepend>
        <q-icon
          name="mdi-calendar-range"
          class="cursor-pointer"
        >
          <q-popup-proxy
            cover
            transition-show="scale"
            transition-hide="scale"
          >
            <q-date
              v-model="dateRange"
              range
              mask="YYYY/MM/DD"
              today-btn
            />
          </q-popup-proxy>
        </q-icon>
      </template>
    </q-input>

    <!-- Chevron next -->
    <q-btn
      v-if="!pickerOnly && !compact"
      icon="mdi-chevron-right"
      flat
      round
      dense
      color="primary"
      @click="navigate(1)"
    />
  </div>
</template>

<style lang="scss" scoped>
.date-range-navigator {
  flex-wrap: nowrap;
  gap: 0.25rem;
}

.date-range-navigator__preset {
  min-width: 9.375rem;
}

.date-range-navigator__input {
  min-width: 10rem;
}

.date-range-navigator--stacked {
  flex-wrap: wrap;

  .date-range-navigator__preset {
    flex: 1 1 100%;
  }

  .date-range-navigator__input {
    flex: 1 1 0;
    min-width: 0;
  }
}

.date-range-navigator__range {
  font-weight: 500;
}

.date-range-navigator__period {
  background: color-mix(in srgb, var(--q-primary) 12%, transparent);
  border-radius: .25rem;
}
</style>
